/**
 * useApi — Base authenticated API hook
 *
 * All API hooks extend from this. Injects Firebase auth token automatically.
 * Provides: request, get, post, put, del, loading, error
 */

import { useState, useCallback, useRef } from "react";
import { useAuth } from "context/AuthContext";
import logger from "services/logger";
import { API_TIMEOUT, JSON_HEADERS } from "config/api";

export class ApiError extends Error {
  constructor(message, status, code, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

export default function useApi() {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllersRef = useRef(new Map());

  const clearError = useCallback(() => setError(null), []);

  const request = useCallback(
    async (url, options = {}) => {
      const requestId = `${options.method || "GET"}-${url}-${Date.now()}`;
      const controller = new AbortController();
      controllersRef.current.set(requestId, controller);

      setLoading(true);
      setError(null);

      const timeoutId = setTimeout(() => controller.abort(), options.timeout || API_TIMEOUT);

      try {
        const token = await getIdToken();
        const headers = {
          ...JSON_HEADERS,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        };

        const res = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
          let body = {};
          try {
            body = await res.json();
          } catch {
            // not JSON
          }
          throw new ApiError(
            body.message || `Request failed with status ${res.status}`,
            res.status,
            body.code || "API_ERROR",
            body
          );
        }

        // Check for empty response
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          return { success: true };
        }

        return await res.json();
      } catch (err) {
        if (err.name === "AbortError") {
          const abortError = new ApiError("Request was cancelled", 0, "ABORTED");
          setError(abortError.message);
          throw abortError;
        }
        if (err instanceof ApiError) {
          logger.error("[useApi]", `${err.status} ${url}:`, err.message);
          setError(err.message);
          throw err;
        }
        logger.error("[useApi]", `Network error on ${url}:`, err.message);
        const apiError = new ApiError(err.message, 0, "NETWORK_ERROR");
        setError(apiError.message);
        throw apiError;
      } finally {
        setLoading(false);
        controllersRef.current.delete(requestId);
      }
    },
    [getIdToken]
  );

  const get = useCallback((url, options = {}) => request(url, { ...options, method: "GET" }), [request]);
  const post = useCallback((url, body, options = {}) => request(url, { ...options, method: "POST", body: JSON.stringify(body) }), [request]);
  const put = useCallback((url, body, options = {}) => request(url, { ...options, method: "PUT", body: JSON.stringify(body) }), [request]);
  const del = useCallback((url, options = {}) => request(url, { ...options, method: "DELETE" }), [request]);

  const cancelAll = useCallback(() => {
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
  }, []);

  const getAuthToken = useCallback(() => getIdToken(), [getIdToken]);

  return { request, get, post, put, del, cancelAll, loading, error, clearError, getAuthToken };
}

/**
 * Factory for domain-specific API hooks.
 */
export function createApiHook(hookFactory) {
  return function useCustomApi() {
    const api = useApi();
    return hookFactory(api);
  };
}
